/**
 * `<caos-split-view>` — the SPLIT (list + detail console) body of a list surface.
 * (Catalog: Data display › SplitView — the SPLIT body of a list surface — the records on the left, the one you are reading on the right.)
 *
 * A two-pane console on a wide viewport: a scrolling left LANE of record cards and a right
 * DETAIL pane that reads the selected record's fields. Selecting a left card shows its detail
 * in place (no route change); the detail header carries an "Open as tab" affordance that opens
 * the full record. Below the medium breakpoint (720px, CAOS-590) the two panes collapse to a single
 * full-width lane and tapping a card opens the record route directly (there is no room for an
 * in-place detail pane).
 *
 * Data in, events out — the same contract `<caos-data-table>` / `<caos-card-grid>` follow. The
 * app supplies `items` (name + a compact `secondary` field set for the lane + the full `detail`
 * field set, all pre-formatted with references resolved). It emits `caos-split-open` (`{ id }`)
 * to open a record (the "Open as tab" button, or a narrow-mode card tap) and `caos-navigate`
 * (`{ href }`) when an in-cell lookup link is clicked — the host routes both through the app's
 * History-API navigation.
 *
 * Styling seams: `::part(wrap)`, `::part(lane)`, `::part(detail)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { atLeast } from '../breakpoints.js';
import type { CaosCardField } from './card-grid.js';
import { makeIcon } from './icons.js';

/** One record in the split view: name + the lane's compact fields + the detail pane's full set. */
export interface CaosSplitItem {
  id: string;
  title: string;
  secondary: CaosCardField[];
  detail: CaosCardField[];
}

const WIDE_QUERY = atLeast('medium');

const sheet = makeSheet(`
  :host { display: block; }

  /* CAOS-652 — padding, gaps and radii come from the spacing and radius scales. The pixel figures
     left in this sheet are intrinsic sizes (the pane height and its bounds, the lane's track
     minimum and maximum, the detail grid's column minimum, the open glyph), the active card's rail,
     hairline borders and type sizes. */

  .wrap {
    display: grid;
    grid-template-columns: 1fr;
    height: clamp(420px, calc(100vh - 300px), 900px);
    min-height: 420px;
    border-top: 1px solid var(--caos-color-border);
    font-family: var(--caos-font);
  }
  .wrap[data-wide="true"] { grid-template-columns: minmax(280px, 360px) 1fr; }

  .lane {
    overflow-y: auto;
    padding: var(--caos-space-2);
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
  }
  .wrap[data-wide="true"] .lane { border-right: 1px solid var(--caos-color-border-strong); }

  /* Left-lane record card — faint hairline at rest; the active card gets an accent border,
     a light accent wash, and a left accent rail (inset shadow). */
  .rc {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
    padding: var(--caos-space-3);
    text-align: left;
    cursor: pointer;
    font: inherit;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .rc:hover { border-color: color-mix(in srgb, var(--caos-color-accent) 40%, transparent); }
  .rc[aria-current="true"] {
    border-color: var(--caos-color-accent);
    background: var(--caos-accent-wash);
    box-shadow: inset 3px 0 0 var(--caos-color-accent);
  }
  .rc:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .rc__name {
    color: var(--caos-color-accent);
    font-weight: 600;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .rc__meta { display: flex; flex-wrap: wrap; gap: var(--caos-space-1) var(--caos-space-3); min-width: 0; }
  .rc__field {
    max-width: 100%;
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail { overflow-y: auto; padding: var(--caos-space-5) clamp(var(--caos-space-4), 2vw, var(--caos-space-6)); }
  .detail__head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--caos-space-3); }
  .detail__title {
    margin: 0;
    min-width: 0;
    font-family: var(--caos-font-display);
    font-size: 20px;
    font-weight: 700;
    color: var(--caos-color-accent);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .detail__open {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-1);
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .detail__open:hover { color: var(--caos-color-accent); }
  .detail__open svg { display: block; width: 14px; height: 14px; }

  .detail__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--caos-space-4);
    margin-top: var(--caos-space-5);
  }
  .dkey {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .dval { margin-top: var(--caos-space-1); font-size: 14px; color: var(--caos-color-text); overflow-wrap: anywhere; }

  a {
    color: var(--caos-color-accent);
    font-weight: 600;
    text-decoration: none;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  a:hover { text-decoration: underline; }
  a:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  .empty { padding: var(--caos-space-5); font-size: 13px; color: var(--caos-color-text-muted); }
`);

export class CaosSplitView extends CaosElement {
  private wrap: HTMLDivElement;
  private lane: HTMLDivElement;
  private detail: HTMLDivElement;
  private _items: CaosSplitItem[] = [];
  private _selectedId: string | null = null;
  private _wide = false;
  private mql: MediaQueryList | null = null;
  private readonly onMedia = (): void => {
    this._wide = this.mql?.matches ?? false;
    this.render();
  };

  constructor() {
    super(sheet);
    this.wrap = document.createElement('div');
    this.wrap.className = 'wrap';
    this.wrap.setAttribute('part', 'wrap');
    this.lane = document.createElement('div');
    this.lane.className = 'lane';
    this.lane.setAttribute('part', 'lane');
    this.detail = document.createElement('div');
    this.detail.className = 'detail';
    this.detail.setAttribute('part', 'detail');
    this.root.append(this.wrap);
  }

  connectedCallback(): void {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mql = window.matchMedia(WIDE_QUERY);
      this._wide = this.mql.matches;
      this.mql.addEventListener('change', this.onMedia);
    }
    this.render();
  }

  disconnectedCallback(): void {
    this.mql?.removeEventListener('change', this.onMedia);
  }

  /** Records (data in — pre-formatted, access-filtered, supplied by the app). */
  get items(): CaosSplitItem[] {
    return this._items;
  }
  set items(list: CaosSplitItem[]) {
    this._items = Array.isArray(list) ? list : [];
    // Keep a valid selection: an unchanged selection stays; otherwise fall to the first record.
    if (!this._items.some((i) => i.id === this._selectedId)) {
      this._selectedId = this._items[0]?.id ?? null;
    }
    this.render();
  }

  private emitOpen(id: string): void {
    this.dispatchEvent(new CustomEvent('caos-split-open', { bubbles: true, composed: true, detail: { id } }));
  }
  private emitNavigate(href: string): void {
    this.dispatchEvent(new CustomEvent('caos-navigate', { bubbles: true, composed: true, detail: { href } }));
  }

  /** Wide: select into the detail pane. Narrow: open the record (no room for a pane). */
  private select(id: string): void {
    if (this._wide) {
      this._selectedId = id;
      this.render();
    } else {
      this.emitOpen(id);
    }
  }

  private render(): void {
    this.wrap.dataset.wide = String(this._wide);
    this.wrap.replaceChildren();

    // ── Left lane ──────────────────────────────────────────────────────────
    this.lane.replaceChildren();
    if (this._items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = 'Nothing to show yet.';
      this.lane.append(empty);
    }
    for (const item of this._items) {
      const rc = document.createElement('button');
      rc.type = 'button';
      rc.className = 'rc';
      rc.dataset.recordId = item.id;
      if (this._wide && item.id === this._selectedId) rc.setAttribute('aria-current', 'true');
      rc.addEventListener('click', () => this.select(item.id));

      const name = document.createElement('span');
      name.className = 'rc__name';
      name.textContent = item.title;
      name.title = item.title;
      rc.append(name);

      const shown = item.secondary.filter((f) => f.value && f.value !== '—');
      if (shown.length) {
        const meta = document.createElement('div');
        meta.className = 'rc__meta';
        for (const f of shown) {
          const span = document.createElement('span');
          span.className = 'rc__field';
          span.textContent = f.value;
          span.title = `${f.label}: ${f.value}`;
          meta.append(span);
        }
        rc.append(meta);
      }
      this.lane.append(rc);
    }
    this.wrap.append(this.lane);

    // ── Detail pane (wide only) ────────────────────────────────────────────
    if (this._wide) {
      this.detail.replaceChildren();
      const item = this._items.find((i) => i.id === this._selectedId) ?? null;
      if (item) {
        const head = document.createElement('div');
        head.className = 'detail__head';
        const h2 = document.createElement('h2');
        h2.className = 'detail__title';
        h2.textContent = item.title;
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'detail__open';
        open.append(document.createTextNode('Open as tab'), this.openIcon());
        open.addEventListener('click', () => this.emitOpen(item.id));
        head.append(h2, open);
        this.detail.append(head);

        const grid = document.createElement('div');
        grid.className = 'detail__grid';
        for (const f of item.detail) {
          const cell = document.createElement('div');
          const key = document.createElement('div');
          key.className = 'dkey';
          key.textContent = f.label;
          const val = document.createElement('div');
          val.className = 'dval';
          if (f.href) val.append(this.link(f.href, f.value));
          else val.textContent = f.value;
          cell.append(key, val);
          grid.append(cell);
        }
        this.detail.append(grid);
      }
      this.wrap.append(this.detail);
    }
  }

  /** A CSP-safe (no innerHTML) navigating anchor. */
  private link(href: string, text: string): HTMLAnchorElement {
    const a = document.createElement('a');
    a.href = href;
    a.textContent = text;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.emitNavigate(href);
    });
    return a;
  }

  /**
   * CAOS-602 — the "opens the full record" mark. This was `arrow-up-right`, drawn here; the app
   * home drew the same arrow independently, which is what one vocabulary is for.
   */
  private openIcon(): SVGSVGElement {
    const svg = makeIcon('arrow-up-right');
    svg.setAttribute('aria-hidden', 'true');
    return svg;
  }
}

define('caos-split-view', CaosSplitView);

declare global {
  interface HTMLElementTagNameMap {
    'caos-split-view': CaosSplitView;
  }
}
